import { IPoint2d } from '@/editor/math/IPoint2d';
import { pixelRatio2d } from '@/editor/math/const';
import { ENTITY_RENDER_ORDER, AdsorbEntitySize, drawPointColor } from '@/editor/constant/entity';
import BaseModel2d from './BaseModel2d';
import { globalStores } from '@/editor/stores';
import { Mesh, MeshBasicMaterial, Path, RingGeometry, Shape, ShapeGeometry } from 'three';
import { EAbsorbPointType } from '@/editor/types/absorb';

export class WallAbsorbPoint extends BaseModel2d {
  private _currentPoint: IPoint2d | null = null;
  private _currentPointType: EAbsorbPointType = EAbsorbPointType.Foot;

  constructor() {
    super();

    this.setDisplayOrder(ENTITY_RENDER_ORDER.wallAbsorbPoint);
    globalStores.editorStateStore.sceneInfo.scene2d.scene.add(this._modelGroup);
  }

  private createModel(position: IPoint2d, zoom: number, pointType: EAbsorbPointType) {
    const scale = 1 / zoom / pixelRatio2d;
    const model = createAbsorbPointModel(pointType, {
      pointRadius: AdsorbEntitySize.PointRadius * scale,
      triangleHeight: AdsorbEntitySize.TriangleHeight * scale,
      squareSize: AdsorbEntitySize.SquareSize * scale,
      crossLength: AdsorbEntitySize.CrossLength * scale,
      thickness: AdsorbEntitySize.Thickness * scale
    });

    model.position.set(position.x / pixelRatio2d, position.y / pixelRatio2d, 0);
    model.renderOrder = ENTITY_RENDER_ORDER.wallAbsorbPoint;
    this._modelGroup.add(model);
  }

  updateZoom(zoom: number) {
    if (this._currentPoint) {
      this.update(this._currentPoint, zoom, this._currentPointType);
    }
  }

  update(position: IPoint2d, zoom: number, pointType: EAbsorbPointType = EAbsorbPointType.Foot) {
    this.clear();
    this._currentPoint = position;
    this._currentPointType = pointType;
    this.createModel(position, zoom, pointType);
  }

  override clear(): void {
    super.clear();
    this._currentPoint = null;
    this._currentPointType = EAbsorbPointType.Foot;
  }
}

interface IAbsorbPointSize {
  pointRadius: number;
  triangleHeight: number;
  squareSize: number;
  crossLength: number;
  thickness: number;
}

function createAbsorbPointModel(type: EAbsorbPointType, size: IAbsorbPointSize) {
  const material = new MeshBasicMaterial({ color: drawPointColor });
  material.depthTest = false;
  const { pointRadius, triangleHeight, squareSize, crossLength, thickness } = size;

  switch (type) {
    case EAbsorbPointType.Center: {
      const outerRadius = (triangleHeight * 2) / 3;
      return new Mesh(
        createTriangleRingGeometry(outerRadius, Math.max(outerRadius - thickness * 1.4, outerRadius * 0.45)),
        material
      );
    }
    case EAbsorbPointType.Start:
    case EAbsorbPointType.End: {
      const outerHalfSize = squareSize / 2;
      return new Mesh(
        createSquareRingGeometry(outerHalfSize, Math.max(outerHalfSize - thickness, outerHalfSize * 0.45)),
        material
      );
    }
    case EAbsorbPointType.Cross:
      return new Mesh(createCrossGeometry(crossLength, thickness), material);
    case EAbsorbPointType.Foot:
    default:
      return new Mesh(
        new RingGeometry(Math.max(pointRadius - thickness, pointRadius * 0.45), pointRadius, 32),
        material
      );
  }
}

function createSquareRingGeometry(outerHalfSize: number, innerHalfSize: number) {
  const square = new Shape();
  square.moveTo(-outerHalfSize, -outerHalfSize);
  square.lineTo(outerHalfSize, -outerHalfSize);
  square.lineTo(outerHalfSize, outerHalfSize);
  square.lineTo(-outerHalfSize, outerHalfSize);
  square.closePath();

  const hole = new Path();
  hole.moveTo(innerHalfSize, -innerHalfSize);
  hole.lineTo(-innerHalfSize, -innerHalfSize);
  hole.lineTo(-innerHalfSize, innerHalfSize);
  hole.lineTo(innerHalfSize, innerHalfSize);
  hole.closePath();
  square.holes.push(hole);

  return new ShapeGeometry(square);
}

function createTriangleRingGeometry(outerRadius: number, innerRadius: number) {
  const triangle = new Shape();
  addRegularPolygonPath(triangle, 3, outerRadius, Math.PI / 2, false);

  const hole = new Path();
  addRegularPolygonPath(hole, 3, innerRadius, Math.PI / 2, true);
  triangle.holes.push(hole);

  return new ShapeGeometry(triangle);
}

function createCrossGeometry(length: number, thickness: number) {
  return new ShapeGeometry([
    createRotatedRectangleShape(length, thickness, Math.PI / 4),
    createRotatedRectangleShape(length, thickness, -Math.PI / 4)
  ]);
}

function createRotatedRectangleShape(length: number, thickness: number, angle: number) {
  const halfLength = length / 2;
  const halfThickness = thickness / 2;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const localPoints = [
    { x: -halfLength, y: -halfThickness },
    { x: halfLength, y: -halfThickness },
    { x: halfLength, y: halfThickness },
    { x: -halfLength, y: halfThickness }
  ];
  const shape = new Shape();

  localPoints.forEach((point, index) => {
    const x = point.x * cos - point.y * sin;
    const y = point.x * sin + point.y * cos;
    if (index === 0) {
      shape.moveTo(x, y);
    } else {
      shape.lineTo(x, y);
    }
  });
  shape.closePath();

  return shape;
}

function addRegularPolygonPath(
  path: Shape | Path,
  sides: number,
  radius: number,
  startAngle: number,
  reverse: boolean
) {
  for (let i = 0; i < sides; i++) {
    const index = reverse ? sides - 1 - i : i;
    const angle = startAngle + (Math.PI * 2 * index) / sides;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;

    if (i === 0) {
      path.moveTo(x, y);
    } else {
      path.lineTo(x, y);
    }
  }
  path.closePath();
}
